import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { homeFor } from './utils/permissions';
import Layout from './components/Layout/Layout';
import PdfPreviewHost from './components/PdfPreview';
import LoginPage from './pages/Login/LoginPage';
import ForgotPasswordPage from './pages/Login/ForgotPasswordPage';
import ResetPasswordPage from './pages/Login/ResetPasswordPage';
import ChangerMotDePassePage from './pages/Login/ChangerMotDePassePage';
import Dashboard from './pages/Dashboard';
import EnfantsList from './pages/Enfants/EnfantsList';
import EnfantForm from './pages/Enfants/EnfantForm';
import EnfantDetail from './pages/Enfants/EnfantDetail';
import DevisList from './pages/Devis/DevisList';
import DevisForm from './pages/Devis/DevisForm';
import DevisDetail from './pages/Devis/DevisDetail';
import ContratsList from './pages/Contrats/ContratsList';
import RHPage from './pages/RH/RHPage';
import FinancePage from './pages/Finance/FinancePage';
import TransmissionsPage from './pages/Transmissions/TransmissionsPage';
import DocumentsPage from './pages/Documents/DocumentsPage';
import AnalyticsPage from './pages/Analytics/AnalyticsPage';
import StatistiquesPage from './pages/Analytics/StatistiquesPage';
import AlertesPage from './pages/Alertes/AlertesPage';
import PreinscriptionsPage from './pages/Preinscriptions/PreinscriptionsPage';
import DemandesPage from './pages/Demandes/DemandesPage';
import ProtocolesPage from './pages/Protocoles/ProtocolesPage';
import RessourcesDocsPage from './pages/Ressources/RessourcesDocsPage';
import SignaturesPage from './pages/Signatures/SignaturesPage';
import DepotPage from './pages/Depot/DepotPage';
import EcheancesPage from './pages/Echeances/EcheancesPage';
import TarifsPage from './pages/Tarifs/TarifsPage';
import UtilisateursPage from './pages/Utilisateurs/UtilisateursPage';
import EspaceEmployePage from './pages/EspaceEmploye/EspaceEmployePage';
import MonEspacePage from './pages/EspaceEmploye/MonEspacePage';
import ChartePage from './pages/Marque/ChartePage';
import ParametresPage from './pages/Parametres/ParametresPage';
import SignerPage from './pages/Signer/SignerPage';
import PlanningPage from './pages/Planning/PlanningPage';
import PlanningEnfantsPage from './pages/Planning/PlanningEnfantsPage';
import SuiviMedicalPage from './pages/SuiviMedical/SuiviMedicalPage';
import CourriersPage from './pages/Courriers/CourriersPage';
import WhatsAppPage from './pages/WhatsApp/WhatsAppPage';
import MentionsLegales from './pages/Legal/MentionsLegales';
import Confidentialite from './pages/Legal/Confidentialite';
import RegistreTraitements from './pages/Legal/RegistreTraitements';

// Garde d'accès : redirige vers la page d'accueil du rôle si le module est interdit
function Guard({ path, children }) {
  const { user, can } = useAuth();
  if (!can(path)) return <Navigate to={homeFor(user.role)} replace />;
  return children;
}

function AuthedApp() {
  const { user } = useAuth();

  // Non connecté : page de connexion (l'espace employé public reste accessible)
  if (!user) {
    return (
      <Routes>
        <Route path="/signer/:token" element={<SignerPage />} />
        <Route path="/espace-employe" element={<EspaceEmployePage />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/confidentialite" element={<Confidentialite />} />
        <Route path="/registre-traitements" element={<RegistreTraitements />} />
        <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
        <Route path="/reinitialiser" element={<ResetPasswordPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  // Mot de passe provisoire (reçu par e-mail) : changement obligatoire avant tout accès.
  if (user.doit_changer_mdp) return <ChangerMotDePassePage />;

  return (
    <AppProvider>
      <Routes>
        <Route path="/signer/:token" element={<SignerPage />} />
        <Route path="/espace-employe" element={<EspaceEmployePage />} />
        <Route path="/mentions-legales" element={<MentionsLegales />} />
        <Route path="/confidentialite" element={<Confidentialite />} />
        <Route path="/registre-traitements" element={<RegistreTraitements />} />
        <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
        <Route path="/reinitialiser" element={<ResetPasswordPage />} />
        <Route path="/" element={<Layout />}>
          <Route index element={<Guard path="/"><Dashboard /></Guard>} />
          <Route path="enfants" element={<Guard path="/enfants"><EnfantsList /></Guard>} />
          <Route path="enfants/nouveau" element={<Guard path="/enfants"><EnfantForm /></Guard>} />
          <Route path="enfants/:id" element={<Guard path="/enfants"><EnfantDetail /></Guard>} />
          <Route path="enfants/:id/modifier" element={<Guard path="/enfants"><EnfantForm /></Guard>} />
          <Route path="suivi-medical" element={<Guard path="/suivi-medical"><SuiviMedicalPage /></Guard>} />
          <Route path="planning-enfants" element={<Guard path="/planning-enfants"><PlanningEnfantsPage /></Guard>} />
          <Route path="courriers" element={<Guard path="/courriers"><CourriersPage /></Guard>} />
          <Route path="whatsapp" element={<Guard path="/whatsapp"><WhatsAppPage /></Guard>} />
          <Route path="devis" element={<Guard path="/devis"><DevisList /></Guard>} />
          <Route path="devis/nouveau" element={<Guard path="/devis"><DevisForm /></Guard>} />
          <Route path="devis/:id/modifier" element={<Guard path="/devis"><DevisForm /></Guard>} />
          <Route path="devis/:id" element={<Guard path="/devis"><DevisDetail /></Guard>} />
          <Route path="contrats" element={<Guard path="/contrats"><ContratsList /></Guard>} />
          <Route path="demandes" element={<Guard path="/demandes"><DemandesPage /></Guard>} />
          <Route path="preinscriptions" element={<Guard path="/preinscriptions"><PreinscriptionsPage /></Guard>} />
          <Route path="tarifs" element={<Guard path="/tarifs"><TarifsPage /></Guard>} />
          <Route path="transmissions" element={<Guard path="/transmissions"><TransmissionsPage /></Guard>} />
          <Route path="mon-espace" element={<Guard path="/mon-espace"><MonEspacePage /></Guard>} />
          <Route path="rh" element={<Guard path="/rh"><RHPage /></Guard>} />
          <Route path="planning" element={<Guard path="/planning"><PlanningPage /></Guard>} />
          <Route path="echeances" element={<Guard path="/echeances"><EcheancesPage /></Guard>} />
          <Route path="finance" element={<Guard path="/finance"><FinancePage /></Guard>} />
          <Route path="documents" element={<Guard path="/documents"><DocumentsPage /></Guard>} />
          <Route path="protocoles" element={<Guard path="/protocoles"><ProtocolesPage /></Guard>} />
          <Route path="ressources-docs" element={<Guard path="/ressources-docs"><RessourcesDocsPage /></Guard>} />
          <Route path="signatures" element={<Guard path="/signatures"><SignaturesPage /></Guard>} />
          <Route path="depot" element={<Guard path="/depot"><DepotPage /></Guard>} />
          <Route path="analytics" element={<Guard path="/analytics"><AnalyticsPage /></Guard>} />
          <Route path="statistiques" element={<Guard path="/statistiques"><StatistiquesPage /></Guard>} />
          <Route path="alertes" element={<Guard path="/alertes"><AlertesPage /></Guard>} />
          <Route path="utilisateurs" element={<Guard path="/utilisateurs"><UtilisateursPage /></Guard>} />
          <Route path="marque" element={<Guard path="/marque"><ChartePage /></Guard>} />
          <Route path="parametres" element={<Guard path="/parametres"><ParametresPage /></Guard>} />
          <Route path="*" element={<Navigate to={homeFor(user.role)} replace />} />
        </Route>
      </Routes>
    </AppProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AuthedApp />
        <PdfPreviewHost />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
